import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Signal,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';

import { LOG } from 'src/app/common/util/log';
import { ActionFn, ActionFunctions, BoTable, TableConstructionData, parseBoTable } from 'src/app/table-handling';

import { CustomDomEvents } from '@utils/events/custom-events';
import { firstValueFrom } from 'rxjs';
import { LoadingIndicatorDirective } from 'src/app/common/directives/loading-indicator.directive';
import { BusinessObjectsElementService } from 'src/openapi-client';
import { GenericTableComponent } from '../generic-table/generic-table.component';

@Component({
  selector: 'app-bo-table',
  standalone: true,
  imports: [CommonModule, GenericTableComponent, LoadingIndicatorDirective],
  templateUrl: './bo-table.component.html',
  styleUrls: ['./bo-table.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BoTableComponent {
  @Input({ required: false }) public errorText: string = 'Unbekannter Fehler: Es ist ein unbekannter Fehler aufgetreten.';

  @Input() public actionHandler: ActionFn | ActionFunctions | undefined;

  @Input({ required: true }) public set documentId(documentId: string) {
    if (documentId === this._documentId()) {
      return;
    }

    this._documentId.set(documentId);
  }

  @Input({ required: false }) public searchTerm: Signal<string | undefined> = signal(undefined);
  @Input({ required: false }) public filterSelections?: Signal<Record<string, string | string[]>>;

  @Output() public tableData: EventEmitter<TableConstructionData> = new EventEmitter();

  protected readonly tableConstructionData: WritableSignal<TableConstructionData | undefined> = signal(undefined);
  protected readonly loadingCounter: WritableSignal<number> = signal(0);
  protected readonly loading: Signal<boolean> = computed(() => this.loadingCounter() > 0);
  protected error?: string;

  private readonly _documentId: WritableSignal<string | undefined> = signal(undefined);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private version: number = 0;

  constructor(private readonly elementService: BusinessObjectsElementService) {
    effect(
      () => {
        const documentId: string = this._documentId();
        if (!documentId || documentId.length === 0) {
          return;
        }
        const filterSelections: Record<string, string | string[]> | undefined = this.filterSelections?.();

        this.loadTable(documentId, filterSelections);
      },
      { allowSignalWrites: true }
    );
  }

  private async loadTable(
    documentId: string,
    filterSelections: Record<string, string | string[]> | undefined
  ): Promise<void> {
    if (!documentId) {
      this.errorHandler('Invalid document id.');
      return;
    }

    const currentVersion: number = ++this.version;

    let filterSelectionsRequest: Record<string, string[]> | undefined = undefined;

    if (filterSelections) {
      filterSelectionsRequest = {};

      for (const key in filterSelections) {
        const element: string | string[] = filterSelections[key];

        filterSelectionsRequest[key] = Array.isArray(element) ? element : [element];
      }
    }

    try {
      this.loadingCounter.update(value => value + 1);
      const result: BoTable = await firstValueFrom(
        this.elementService.fetchFirstTable(documentId, undefined, filterSelectionsRequest)
      );
      if (this.version === currentVersion) {
        const parsedData: TableConstructionData = parseBoTable(result);
        this.tableConstructionData.set(parsedData);
        this.tableData.next(parsedData);
        this.error = undefined;
      }
    } catch (e) {
      const loadErrorEvent = new CustomEvent<Error>(CustomDomEvents.ELEMENT_LOAD_ERROR, { detail: e, bubbles: true });
      this.elementRef.nativeElement.dispatchEvent(loadErrorEvent);

      if (this.version === currentVersion) {
        this.errorHandler(e);
      }
    } finally {
      this.loadingCounter.update(value => value - 1);
    }
  }

  private readonly errorHandler = (msg: unknown): void => {
    this.error = `${msg}`;
    LOG.error(msg);
  };
}
